import { Trans, useTranslation } from 'react-i18next';
import {
  FontWeight,
  Heading,
  LiliLinkButton,
  LiliLinkType,
  LiliButton,
  LiliButtonType,
  LiliNotification,
  Text,
} from '@lili/lili-frontend-common';
import { useBlocker } from '@tanstack/react-router';
import { useUnifiedKybPolling } from '@Hooks/polling/useUnifiedKybPolling';
import { Icon, Icons } from '@Components/icon/Icon';
import logoUrl from '@Assets/logos/lili-logo-blue.svg';
import { isLiliAppVersionAtLeast } from '@Services/utils/utils.ts';
import { useBoundStore } from '@Store/useBoundStore.ts';
import { DeviceType } from '@Types/onboarding.types.ts';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types';
import { NonUsSocureLogicHook } from './NonUsSocureLogic.hook';
import { MainContent } from './components/nonUsSocureMainContent';
import { actOnNonUseSocure } from './NonUsSocureAnalytics';

export interface SocureLogicHookResult {
  onSendSmsClick: () => void;
  openResendOption: boolean;
  timeLeft: string | null;
  isLoading: boolean;
  handleFinishedVerification?: () => void;
  errorMessage?: string;
  socureQR?: string;
  isSuccessNotificationVisible: boolean;
  setIsSuccessNotificationVisible: (visible: boolean) => void;
}

export interface NonUSSocurePageProps {
  socureData: { qrCode?: string; url?: string };
}

export interface NonUSSocurePageWithLogicProps {
  socureData: { qrCode?: string; url?: string };
  logic: SocureLogicHookResult;
  skipDefaultPolling?: boolean;
  customDescription?: string;
}

export const NonUSSocurePageLayout = ({
  socureData,
  logic,
  skipDefaultPolling = false,
  customDescription,
}: NonUSSocurePageWithLogicProps) => {
  const { t } = useTranslation('nonUsSocure');

  const {
    onSendSmsClick,
    openResendOption,
    timeLeft,
    isLoading: isFetchingQrData,
    handleFinishedVerification,
    errorMessage,
    socureQR,
    isSuccessNotificationVisible,
    setIsSuccessNotificationVisible,
  } = logic;

  const socureQRCombined = socureQR || socureData?.qrCode;
  const socureUrlCombined = socureData?.url;

  const defaultPolling = useUnifiedKybPolling(
    handleFinishedVerification,
    5400000,
    2700,
    !skipDefaultPolling
  );
  const { isLoading, isRefetching } = defaultPolling;

  const { isFromLiliApp, deviceType } = useBoundStore((state) => state.onboardingSlice);

  const smsText = !openResendOption ? t('mainContent.sendTextMessage') : t('mainContent.sendAgain');

  return (
    <div className='w-full h-full items-center flex flex-col pt-[72px] sm:px-[40px] overflow-y-auto'>
      <div className='flex flex-col max-w-[600px] h-full px-[24px] sm:px-[48px]'>
        <div className='flex flex-col items-center h-full' id='page-content'>
          <div className='flex flex-col h-full'>
            {isSuccessNotificationVisible && (
              <LiliNotification
                id='success-notification'
                text={t('mainContent.newLinkCreatedSuccess')}
                type={2}
                onClose={() => setIsSuccessNotificationVisible(false)}
              />
            )}
            <img className='w-[70px] mb-[32px] flex-shrink-0' src={logoUrl} alt='Lili logo' />
            <Heading.H5 id='stepPageHeader' fontWeight={FontWeight.SEMIBOLD}>
              {t('pageContent.title')}
            </Heading.H5>

            <Text.H9 className='whitespace-pre-line mb-[24px] mt-[8px]'>
              {customDescription || (
                <Trans
                  ns='nonUsSocure'
                  i18nKey='pageContent.paragraphOne'
                  components={{
                    bold: <span className='font-metricSemibold phone:hidden' />,
                    break: <br />,
                  }}
                />
              )}
            </Text.H9>

            <Text.H9 className='text-text-main-primary whitespace-pre-line mb-[24px] mt-[8px]'>
              {t('pageContent.paragraphTwo')}
            </Text.H9>

            <MainContent
              isLoading={isFetchingQrData || isLoading}
              onSendSmsClick={onSendSmsClick}
              openResendOption={openResendOption}
              timeLeft={timeLeft}
              smsText={smsText}
              socureQR={socureQRCombined}
            />

            {!timeLeft && (
              <LiliLinkButton
                onClick={() => {
                  if (!timeLeft) {
                    onSendSmsClick();
                  }
                }}
                id='get-new-link'
                type={LiliLinkType.QUIET}
                title={
                  timeLeft
                    ? t('mainContent.sendAgainWithTimer', { timeLeft })
                    : t('mainContent.needNewLink')
                }
                className='mt-[32px] mx-auto'
              />
            )}

            <div>
              {errorMessage && !isLoading && !isRefetching && (
                <Text.H11 className='text-text-negative-red whitespace-pre-line mb-[24px] mt-[8px]'>
                  *{errorMessage}
                </Text.H11>
              )}
            </div>
            <div className='phone:none phone:hidden w-full mt-auto mb-8 z-40 bg-surface-primary-bw-00 sm:block shrink-0'>
              <div className='w-full flex items-center justify-center mx-auto'></div>
            </div>

            <div className='sm:hidden w-full mt-auto mb-8 z-40 bg-surface-primary-bw-00 shrink-0'>
              <div className='w-full flex items-center justify-center mx-auto'>
                <div className='w-full max-w-[542px]'>
                  <LiliButton
                    onClick={() => {
                      actOnNonUseSocure('open external socure link');
                      if (!socureUrlCombined) return;
                      if (isFromLiliApp && isLiliAppVersionAtLeast('26.2.1')) {
                        if (deviceType === DeviceType.IOS) {
                          window.location.replace(
                            'liliapp://socure?url=' + encodeURIComponent(socureUrlCombined)
                          );
                          return;
                        }
                        if (deviceType === DeviceType.ANDROID) {
                          window.androidApp.externalSocure(socureUrlCombined);
                          return;
                        }
                      }

                      window.open(socureUrlCombined, '_blank');
                    }}
                    id={'start-verification-process'}
                    type={LiliButtonType.PRIMARY}
                    title={t('pageContent.mobileButtonTitle')}
                    suffixIcon={<Icon icon={Icons.Expand} className='fill-text-brand-primary' />}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const NonUSSocurePage = ({ socureData }: NonUSSocurePageProps) => {
  const logic = NonUsSocureLogicHook();

  useBlocker({
    shouldBlockFn: ({ next }) => {
      const allowedRoutes: string[] = [
        routesModel[RoutesMap.SUCCESS].path,
        routesModel[RoutesMap.SUCCESS_WITHOUT_AUTO_LOGIN].path,
        routesModel[RoutesMap.KYC_FAILED].path,
        routesModel[RoutesMap.VERIFICATION_CENTER].path,
        routesModel[RoutesMap.UNDER_REVIEW].path,
      ];
      return !allowedRoutes.includes(next.routeId);
    },
  });

  return <NonUSSocurePageLayout socureData={socureData} logic={logic} />;
};
